import React, {
  Fragment,
  useEffect,
  useCallback,
  useRef,
  useState,
  useContext,
} from 'react';
import {
  getPersonalCoachPrice,
  mapToChallengeAddOnProducts,
  isCardPaymentMethod,
} from '../utils/challengeAddon.util';
import ContentCardLabel from 'src/components/content/content-card-label';
import { Product } from 'src/pages/deprecated/payment/types';
import ChallengeAddOnCard from '../components/ChallengeAddonCard';
import { ChallengeAddOnProduct } from '../types/challenge.types';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  setChallengeAddOnProduct,
  setChallengeModalOpen,
  setIsPersonalCoachAddonAdded,
  setPersonalCoachPriceAmount,
  setPersonalCoachPriceId,
} from 'src/store/payment';
import { ChallengeAddOnSummaryModal } from '../modal/SelectAddonModal';
import { ShippingAddressModal } from '../modal/ShippingAddressModal';
import { AddonPurchaseConfirmation } from '../modal/PurchaseConfirmation';
import { PaymentMethod } from '@stripe/stripe-js';
import { checkAuthentication } from 'src/lib/auth/actions';
import { useDispatch } from 'react-redux';
import paymentAPI from 'src/pages/deprecated/payment/api';
import ProductService from 'src/services/product-service';
import { useChallengeAddonModals } from '../hooks/useChallengeAddon';
import { PLANS } from 'src/pages/constants';
import { loadStripe } from '@stripe/stripe-js';
import { ToastContext } from 'src/components/toast/toast';
import { Stripe } from '@stripe/stripe-js';
import { stripePromise as sharedStripePromise } from '../context/StripeContext';
import { isUserFromAustralia } from 'src/lib/utils';
import { useDecision } from '@optimizely/react-sdk';
import { useChallengeProducts } from 'src/pages/subscription-new/hooks/useChallengeProducts';
import {
  trackAddonSelected,
  trackAddonPaymentStepViewed,
  trackAddonPaymentMethodSelected,
  trackAddonPaymentSubmitted,
  trackAddonPaymentFailed,
  trackAddonPaymentCompleted,
  trackAddonPurchaseCompleted,
} from 'src/util/track';

type ChallengeAddOnsProps = {
  products: Product[];
  refetchProduct: () => void;
  paymentMethod: PaymentMethod | undefined;
  stripePromise: Promise<Stripe | null>;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  hideChallengePersonalCoachAddon: boolean;
};

export const ChallengeAddOns: React.FC<ChallengeAddOnsProps> = ({
  products,
  paymentMethod,
  refetchProduct,
  stripePromise,
  setPaymentMethod,
  hideChallengePersonalCoachAddon = false,
}) => {
  // Trigger fetching challenge data
  useChallengeProducts();

  const dispatch = useAppDispatch();
  const reduxDispatch = useDispatch();
  const { addToast } = useContext(ToastContext);
  const [{ enabled: isAfterPayDisabled }] = useDecision(
    'disable_afterpay_all_flow',
    {
      autoUpdate: true,
    }
  );

  const defaultPaymentMethod =
    isUserFromAustralia() && !isAfterPayDisabled
      ? isCardPaymentMethod(paymentMethod)
        ? 'card'
        : 'afterpay'
      : 'card';

  const [selectedPaymentType, setSelectedPaymentType] = useState<
    'card' | 'afterpay'
  >(defaultPaymentMethod);
  const paymentType = selectedPaymentType;

  const handlePaymentTypeChange = (type: 'card' | 'afterpay') => {
    trackAddonPaymentMethodSelected(
      type,
      challengeAddOnProduct?.product?.name || 'challenge_addons'
    );
    setSelectedPaymentType(type);
  };

  const [isProcessing, setIsProcessing] = useState(false);

  const {
    challengeModalOpen,
    personalCoachPriceId,
    challengeAddOnProduct,
    isPersonalCoachingAdded,
  } = useAppSelector(state => state.payment);

  const { user } = useAppSelector(state => state.auth);

  const challengeProducts = mapToChallengeAddOnProducts(products);
  const urlParamProcessedRef = useRef<string | boolean>(false);

  const handleAddClick = useCallback(
    (challengeProduct: ChallengeAddOnProduct) => {
      const personalCoachPrice = getPersonalCoachPrice(products);
      const total =
        challengeProduct.amount +
        (isPersonalCoachingAdded ? personalCoachPrice?.unitAmount ?? 0 : 0);
      trackAddonPaymentStepViewed(challengeProduct.product.name, total);
      dispatch(setChallengeModalOpen({ isAddonContentModalOpen: true }));
      dispatch(setChallengeAddOnProduct(challengeProduct));
      dispatch(setIsPersonalCoachAddonAdded(false));
    },
    [dispatch, products, isPersonalCoachingAdded]
  );

  const completeAfterpayPayment = async (clientSecret: string) => {
    const personalCoachPrice = getPersonalCoachPrice(products);
    const totalAmount =
      (challengeAddOnProduct?.amount ?? 0) +
      (isPersonalCoachingAdded ? personalCoachPrice?.unitAmount ?? 0 : 0);
    const selectedPriceIds = [
      challengeAddOnProduct?.priceId,
      isPersonalCoachingAdded ? personalCoachPriceId : null,
    ].filter(Boolean);

    try {
      const stripe = await loadStripe(
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
      );

      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { error, paymentIntent } =
        await stripe.confirmAfterpayClearpayPayment(clientSecret, {
          payment_method: {
            billing_details: {
              name: user?.firstName
                ? `${user.firstName} ${user?.lastName || ''}`.trim()
                : user?.email || '',
              email: user?.email || '',
              address: {
                country: 'AU',
              },
            },
          },
          return_url: `${window.location.origin}/account/addons?afterpay_okay=true`,
          mandate_data: await ProductService.getCustomerAcceptance(),
        });

      if (error) {
        trackAddonPaymentFailed(
          'afterpay',
          challengeAddOnProduct?.product?.name || 'challenge_addons',
          'payment_authorization',
          error.message || 'Afterpay payment failed',
          totalAmount
        );
        addToast(error.message || 'Afterpay payment failed', 'danger', 5000);
        throw new Error('Afterpay payment failed');
      }

      if (paymentIntent?.status === 'succeeded') {
        const completeResponse = await paymentAPI.post(
          '/afterpay/complete-addon-purchase',
          {
            paymentIntentId: paymentIntent.id,
            userId: user?.id,
          }
        );

        if (completeResponse.data.data === true) {
          trackAddonSelected(
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            'afterpay',
            challengeAddOnProduct?.amount || 0
          );
          if (isPersonalCoachingAdded) {
            trackAddonSelected(
              'personal_nutrition_coach',
              'afterpay',
              personalCoachPrice?.unitAmount || 0
            );
          }
          trackAddonPaymentCompleted(
            'afterpay',
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            totalAmount
          );
          trackAddonPurchaseCompleted(
            'afterpay',
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            totalAmount,
            selectedPriceIds.join(',')
          );

          dispatch(setChallengeModalOpen({ isConfirmationModalOpen: true }));
          refetchProduct();
          addToast('Purchase completed successfully!', 'success', 5000);
        } else {
          const message =
            completeResponse.data.message || 'Failed to complete purchase';
          trackAddonPaymentFailed(
            'afterpay',
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            'afterpay_completion',
            message,
            totalAmount
          );
          addToast(message, 'danger', 5000);
          throw new Error(message);
        }
      } else {
        trackAddonPaymentFailed(
          'afterpay',
          challengeAddOnProduct?.product?.name || 'challenge_addons',
          'payment_authorization',
          'Payment was not completed',
          totalAmount
        );
        addToast('Payment was not completed', 'danger', 5000);
        throw new Error('Payment was not completed');
      }
    } catch (error) {
      const message =
        (error as any)?.message || 'Failed to complete afterpay payment';
      trackAddonPaymentFailed(
        'afterpay',
        challengeAddOnProduct?.product?.name || 'challenge_addons',
        'afterpay_completion',
        message,
        totalAmount
      );
      addToast(message, 'danger', 5000);
      throw new Error(message);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      addToast('User not found', 'danger', 5000);
      return;
    }

    const selectedPriceIds = [
      challengeAddOnProduct?.priceId,
      isPersonalCoachingAdded ? personalCoachPriceId : null,
    ].filter(Boolean);

    if (!selectedPriceIds.length) {
      addToast('Please select at least one addon', 'danger', 5000);
      return;
    }

    const personalCoachPrice = getPersonalCoachPrice(products);
    const totalAmount =
      (challengeAddOnProduct?.amount ?? 0) +
      (isPersonalCoachingAdded ? personalCoachPrice?.unitAmount ?? 0 : 0);

    trackAddonPaymentSubmitted(
      paymentType,
      challengeAddOnProduct?.product?.name || 'challenge_addons',
      totalAmount
    );

    setIsProcessing(true);

    try {
      const { data } = await paymentAPI.post('stripe/customer/create', {
        email: user.email,
        userID: user.id,
      });

      const invoicePayload = {
        paymentMethodID: paymentType === 'card' ? paymentMethod?.id : undefined,
        customerID: data.customer?.id,
        priceIDs: selectedPriceIds,
        promoID: null,
        userID: user.id,
        isChallenge: false,
        isChallengePackage: true,
        isAfterPay: paymentType === 'afterpay',
      };

      const purchaseResponse = await paymentAPI.post(
        'stripe/invoice/purchase',
        invoicePayload
      );

      if (purchaseResponse.data.isSuccess) {
        if (paymentType === 'afterpay') {
          if (purchaseResponse.data.requiresPaymentCompletion) {
            const { clientSecret } = purchaseResponse.data;
            await completeAfterpayPayment(clientSecret);
          } else {
            trackAddonSelected(
              challengeAddOnProduct?.product?.name || 'challenge_addons',
              'afterpay',
              challengeAddOnProduct?.amount || 0
            );
            if (isPersonalCoachingAdded) {
              trackAddonSelected(
                'personal_nutrition_coach',
                'afterpay',
                personalCoachPrice?.unitAmount || 0
              );
            }
            trackAddonPaymentCompleted(
              'afterpay',
              challengeAddOnProduct?.product?.name || 'challenge_addons',
              totalAmount
            );
            trackAddonPurchaseCompleted(
              'afterpay',
              challengeAddOnProduct?.product?.name || 'challenge_addons',
              totalAmount,
              selectedPriceIds.join(',')
            );
            refetchProduct();
          }
        } else {
          trackAddonSelected(
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            'card',
            challengeAddOnProduct?.amount || 0
          );
          if (isPersonalCoachingAdded) {
            trackAddonSelected(
              'personal_nutrition_coach',
              'card',
              personalCoachPrice?.unitAmount || 0
            );
          }
          trackAddonPaymentCompleted(
            'card',
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            totalAmount
          );
          trackAddonPurchaseCompleted(
            'card',
            challengeAddOnProduct?.product?.name || 'challenge_addons',
            totalAmount,
            selectedPriceIds.join(',')
          );
          dispatch(setChallengeModalOpen({ isConfirmationModalOpen: true }));
          refetchProduct();
        }
      } else {
        const message = purchaseResponse.data.message || 'Purchase failed';
        trackAddonPaymentFailed(
          paymentType,
          challengeAddOnProduct?.product?.name || 'challenge_addons',
          'purchase_request',
          message,
          totalAmount
        );
        addToast(message, 'danger', 5000);
        throw new Error(message);
      }
    } catch (error) {
      console.error('Payment failed:', error);
      const errorMsg =
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Payment failed. Please try again.';
      trackAddonPaymentFailed(
        paymentType,
        challengeAddOnProduct?.product?.name || 'challenge_addons',
        'purchase_request',
        errorMsg,
        totalAmount
      );
      addToast(errorMsg, 'danger', 5000);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const {
    isLoading,
    handleCTAClickModal1,
    handleCTAClickModal2,
    handleCloseAll,
    handleCloseModal3,
  } = useChallengeAddonModals({ handlePurchase, refetchProduct });

  useEffect(() => {
    if (!personalCoachPriceId) {
      const personalCoachPrice = getPersonalCoachPrice(products);
      if (personalCoachPrice) {
        const { unitAmount, priceId } = personalCoachPrice;
        unitAmount && dispatch(setPersonalCoachPriceAmount(unitAmount));
        priceId && dispatch(setPersonalCoachPriceId(priceId));
      }
    }
  }, [dispatch, personalCoachPriceId, products]);

  useEffect(() => {
    reduxDispatch(checkAuthentication());
  }, [reduxDispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let productParam = urlParams.get('product');

    // fallback: check sessionStorage for product parameter from SSO
    if (!productParam) {
      const ssoProductParam = sessionStorage.getItem('sso_product_param');
      if (ssoProductParam) {
        productParam = ssoProductParam;
        // clear it after use
        sessionStorage.removeItem('sso_product_param');
      }
    }

    if (productParam && !urlParamProcessedRef.current) {
      urlParamProcessedRef.current = productParam;
    }
  }, []);

  useEffect(() => {
    const storedProductParam = urlParamProcessedRef.current;

    if (
      typeof storedProductParam === 'string' &&
      challengeProducts.length > 0
    ) {
      let targetProduct: ChallengeAddOnProduct | undefined;

      if (storedProductParam === 'access') {
        targetProduct = challengeProducts.find(
          product => product.product.name === PLANS.CHALLENGE_ONLY
        );
      } else if (storedProductParam === 'bundle') {
        targetProduct = challengeProducts.find(
          product => product.product.name === PLANS.CHALLENGE_WITH_SUPPLEMENTS
        );
      } else if (storedProductParam === 'coach') {
        user?.isChallengeUser
          ? (targetProduct = challengeProducts.find(
              product => product.product.name === PLANS.PERSONAL_COACH
            ))
          : (targetProduct = challengeProducts.find(
              product => product.product.name === PLANS.CHALLENGE_ONLY
            ));
      }

      if (targetProduct) {
        urlParamProcessedRef.current = true; // Mark as processed
        handleAddClick(targetProduct);
      }
    }
  }, [challengeProducts, handleAddClick, user?.isChallengeUser]);

  return (
    <>
      <ContentCardLabel>
        <div>Challenge</div>
      </ContentCardLabel>
      {challengeProducts.map(challengeProduct => {
        const price = Object.values(
          challengeProduct?.product?.prices || {}
        ).find(p => p.active);

        if (!price) return null;

        return (
          <Fragment key={price.id}>
            <ChallengeAddOnCard
              name={challengeProduct?.name}
              price={challengeProduct?.amount.toFixed(0)}
              description={challengeProduct?.description}
              onAdd={() => handleAddClick(challengeProduct)}
              isLoading={isProcessing}
            />
          </Fragment>
        );
      })}
      {Boolean(challengeModalOpen?.isAddonContentModalOpen) && (
        <ChallengeAddOnSummaryModal
          hidePersonalCoachAddon={hideChallengePersonalCoachAddon}
          handleCTAClick={handleCTAClickModal1}
          onClose={() => handleCloseAll('isAddonContentModalOpen')}
          isLoading={isLoading || isProcessing}
          paymentMethod={paymentMethod}
          stripePromise={sharedStripePromise}
          setPaymentMethod={setPaymentMethod}
          onPaymentTypeChange={handlePaymentTypeChange}
        />
      )}
      {Boolean(challengeModalOpen?.isShippingAddressModalOpen) && (
        <ShippingAddressModal
          handleCTAClick={handleCTAClickModal2}
          onClose={() => handleCloseAll('isShippingAddressModalOpen')}
          isLoading={isLoading || isProcessing}
          paymentMethod={paymentMethod}
          stripePromise={sharedStripePromise}
          setPaymentMethod={setPaymentMethod}
          onPaymentTypeChange={handlePaymentTypeChange}
        />
      )}
      {Boolean(challengeModalOpen?.isConfirmationModalOpen) && (
        <AddonPurchaseConfirmation onClose={handleCloseModal3} />
      )}
    </>
  );
};
